import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { POSTS_PROJECT_TYPES, PostsProjectType } from '@ametista-ai/api-schema/constants';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Group, Input, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useClients } from '@/queries/clients/useClients';
import { useCreatePost } from '@/queries/posts/useCreatePost';
import { useTemplates } from '@/queries/templates/useTemplates';
import { useStore } from '@/stores';
import { DEFAULT_CLIENT_TIMEZONE } from '@/utils/timezones';
import { SimpleContentField } from '../ContentField/SimpleContentField';
import { DocumentationField } from '../DocumentationField/DocumentationField';
import {
  buildChecklistPreview,
  ChecklistPreviewTask,
  recomputeChecklistDueDates,
} from './templateChecklist';
import { TemplateChecklistPreview } from './TemplateChecklistPreview';

dayjs.extend(utc);
dayjs.extend(timezone);

function getDefaultDateForClient(
  startDate: dayjs.Dayjs | undefined,
  preferredTime: string | null | undefined,
  clientTimezone: string
) {
  let hours = 13;
  let minutes = 0;
  if (preferredTime) {
    const [h, m] = preferredTime.split(':');
    const parsedH = Number(h);
    const parsedM = Number(m ?? '0');
    if (!Number.isNaN(parsedH) && parsedH >= 0 && parsedH <= 23) {
      hours = parsedH;
    }
    if (!Number.isNaN(parsedM) && parsedM >= 0 && parsedM <= 59) {
      minutes = parsedM;
    }
  }
  const dateOnly = (startDate ?? dayjs()).format('YYYY-MM-DD');
  const time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return dayjs.tz(`${dateOnly} ${time}`, clientTimezone);
}

export const PostForm = observer(
  ({
    clientId,
    initialDate,
    initialContent,
    initialDocumentation,
    initialTitle,
  }: {
    clientId?: string;
    initialDate?: dayjs.Dayjs;
    initialTitle?: string;
    initialContent?: string;
    initialDocumentation?: string;
  }) => {
    const { t } = useTranslation();
    const store = useStore();
    const navigate = useNavigate();
    const { data: clientsData } = useClients();
    const { data: templates } = useTemplates();
    const { mutateAsync: createPost, isPending: isCreatingPost } = useCreatePost();
    const [checklist, setChecklist] = useState<ChecklistPreviewTask[]>([]);
    // RichEditor/RichEditorSimple only read `initialContent` once, at mount
    // (it seeds a `useState` lazy initializer, not a controlled value) — so
    // remount the caption/documentation editors on template selection,
    // otherwise `handleTemplateChange`'s `form.setFieldValue` prefill would
    // update `form.values` without the editor ever showing it.
    const [templateVersion, setTemplateVersion] = useState(0);

    const selectedClientId = clientId ?? store.createPostModal.clientId;
    const selectedClient = clientsData?.data.clients.find((c) => c.id === selectedClientId);

    const form = useForm({
      mode: 'controlled',
      initialValues: {
        clientId: clientId ?? '',
        templateId: '',
        title: initialTitle ?? '',
        content: initialContent ?? '',
        documentation: initialDocumentation ?? '',
        date: getDefaultDateForClient(
          initialDate,
          selectedClient?.preferredTime,
          selectedClient?.timezone ?? DEFAULT_CLIENT_TIMEZONE
        ).format(),
        type: PostsProjectType.POST as PostsProjectType,
      },
      validate: {
        title: (value) => (value.length > 0 ? null : t('post.title-required')),
        date: (value) => (value ? null : t('post.date-required')),
      },
    });

    const handleTemplateChange = useCallback(
      (templateId: string | null) => {
        form.setFieldValue('templateId', templateId ?? '');
        setTemplateVersion((version) => version + 1);
        const template = templates?.find((t) => t.id === templateId);
        if (!template) {
          setChecklist([]);
          return;
        }
        form.setFieldValue('title', template.title);
        form.setFieldValue('content', template.description ?? '');
        form.setFieldValue('documentation', template.documentation ?? '');
        setChecklist(buildChecklistPreview(template, dayjs(form.values.date)));
      },
      [form, templates]
    );

    useEffect(() => {
      setChecklist((prev) =>
        prev.length ? recomputeChecklistDueDates(prev, dayjs(form.values.date)) : prev
      );
    }, [form.values.date]);

    const deliverableTypes = useMemo(() => {
      const configuredTypes = new Set(
        (selectedClient?.postsConfig?.projectTypes ?? []).map((config) => config.type)
      );
      return [PostsProjectType.POST, ...configuredTypes].map((type) => ({
        value: type,
        label: POSTS_PROJECT_TYPES[type].name,
      }));
    }, [selectedClient]);

    const handleSubmit = useCallback(
      async (values: typeof form.values) => {
        const response = await createPost({
          clientId: values.clientId,
          title: values.title,
          content: values.content,
          documentation: values.documentation,
          date: dayjs(values.date).utc().toDate(),
          type: values.type,
          checklist: checklist.length
            ? checklist.map((task) => ({
                id: task.id,
                title: task.title,
                userId: task.userId,
                dueDate: task.dueDate,
                blockedBy: task.blockedBy,
                blockedByPostApproval: task.blockedByPostApproval,
              }))
            : undefined,
        });
        const post = response.data.post;

        if (!store.createPostModal.createMorePosts) {
          navigate(`/post/${post.id}`);
          store.createPostModal.close();
        } else {
          form.reset();
          setChecklist([]);
          store.createPostModal.reset(false);
          store.createPostModal.setClientId(post.clientId);
          store.createPostModal.bumpPostVersion();
        }
      },
      [form, createPost, checklist, store.createPostModal, navigate]
    );

    const clients = useMemo(() => {
      return (clientsData?.data.clients ?? [])
        .filter((client) => !client.archived || client.id === selectedClientId)
        .map((client) => ({
          label: client.name,
          value: client.id,
        }));
    }, [clientsData, selectedClientId]);

    return (
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Select
            label={t('post.client-label')}
            data={clients}
            value={form.values.clientId}
            onChange={(value) => {
              form.setFieldValue('clientId', value ?? '');
              store.createPostModal.setClientId(value ?? '');
            }}
          />

          {!!templates?.length && (
            <Select
              label={t('post.template-label')}
              placeholder={t('post.template-placeholder')}
              data={templates.map((template) => ({ value: template.id, label: template.title }))}
              value={form.values.templateId || null}
              clearable
              onChange={handleTemplateChange}
            />
          )}

          {deliverableTypes.length > 1 && (
            <Select
              label={t('post.type-label')}
              data={deliverableTypes}
              value={form.values.type}
              allowDeselect={false}
              onChange={(value) => value && form.setFieldValue('type', value as PostsProjectType)}
            />
          )}

          <TextInput
            data-autofocus
            label={t('post.title-placeholder')}
            {...form.getInputProps('title')}
          />

          <Input.Wrapper label={t('post.date-label')}>
            <DateTimePicker placeholder={t('posts.post-date')} {...form.getInputProps('date')} />
          </Input.Wrapper>

          <Stack gap={0}>
            <Text size="sm" fw={500}>
              {t('posts.content')}
            </Text>
            <SimpleContentField
              key={templateVersion}
              content={form.values.content}
              onChange={(content) => form.setFieldValue('content', content)}
            />
          </Stack>

          <Stack gap={0}>
            <Text size="sm" fw={500}>
              {t('posts.documentation')}
            </Text>
            <DocumentationField
              key={templateVersion}
              content={form.values.documentation}
              onChange={(documentation) => form.setFieldValue('documentation', documentation)}
            />
          </Stack>

          <TemplateChecklistPreview tasks={checklist} onChange={setChecklist} />

          <Group justify="flex-end" gap="sm">
            <Switch
              checked={store.createPostModal.createMorePosts}
              onChange={(event) =>
                store.createPostModal.setCreateMorePosts(event.currentTarget.checked)
              }
              label={t('post.create-more-posts')}
            />
            <Button
              type="submit"
              disabled={!form.isValid() || isCreatingPost}
              loading={isCreatingPost}
            >
              {t('post.create-button')}
            </Button>
          </Group>
        </Stack>
      </form>
    );
  }
);
